// view-children.decorator.ts
import {ViewportScroller} from '@angular/common';
import {HttpParams} from '@angular/common/http';
import {inject} from '@angular/core';
import {ActivatedRoute, Params, QueryParamsHandling} from '@angular/router';
import {BaseComponent, ListTableService, NgxEasyTablePagination, PageApiResponse} from 'compob2b-libs';
import {Columns, Config, DefaultConfig, Event} from 'compob2b-table';
import {PaginationInstance} from 'ngx-pagination';
import {CrudConfig} from '../../../../compob2b-admin/src/app/admin-shared/interfaces/crud.config';
import {HeaderSearchEvent} from '../../../../compob2b-table/lib/components/header/header.component';
import {PaginationRange} from '../../../../compob2b-table/lib/components/pagination/pagination.component';

export abstract class ListTable extends BaseComponent {
  columns: Columns[] = [];
  params: HttpParams = new HttpParams();
  scrollTop = true;
  uriView: string;
  pagination: NgxEasyTablePagination = {
    limit: this.environment.gridPaginationRange,
    offset: 0,
    count: -1,
    direction: 'header',
    order: 'asc',
    search: '',
    field: ''
  };
  config: CrudConfig;
  configuration: Config = {...DefaultConfig};
  uri: string;
  showDeleted = false;
  route = inject(ActivatedRoute);
  tableService = inject(ListTableService);
  selected = new Set<number>();
  selectedRows = new Map<number, any>();
  viewportScroller = inject(ViewportScroller);

  handleResponse(page: PageApiResponse<unknown>): void {
    if (!page) {
      this.configuration.paginationEnabled = false;
      this.pagination.count = 0;
    } else {
      this.configuration.paginationEnabled = page.totalPages > 1 || this.route.snapshot.queryParams.limit;
      this.pagination.limit = page.size;
      this.pagination.offset = page.pageable.pageNumber;
      this.pagination.count = page.totalElements;
      if (this.pagination.maxTotalElements) {
        this.pagination.count = Math.min(this.pagination.count, this.pagination.maxTotalElements);
      }

      this.pagination = {...this.pagination};
    }

    this.configuration.isLoading = false;
    this.loading = false;

    if (this.scrollTop) {
      window.scrollTo(0, 0);
    }
    this.scrollTop = true;
    this.cdr.detectChanges();
  }

  get totalElements(): number {
    return this.pagination.count > 0 ? this.pagination.count : 0;
  }

  updatePagination(params: Params): void {
    this.pagination = {
      ...this.pagination,
      offset: params.page === undefined ? 0 : (Number(params.page) - 1),
      limit: Number(params.limit) || this.pagination.limit,
      order: params.order || this.pagination.order,
      direction: params.direction || this.pagination.direction,
      count: -1
    };
  }

  protected getListPageURI(base: string = null): string {
    let uri = (base || this.uri) + '/page/' + this.pagination.offset + '/';
    if (this.pagination.limit !== null) {
      uri += this.pagination.limit + '/';
    }

    return uri;
  }

  protected getListURI(base: string = null): string {
    let uri = this.getListPageURI(base);

    if (this.pagination.order) {
      uri += this.pagination.order + '/';
    }
    if (this.pagination.direction) {
      uri += this.pagination.direction + '/';
    }

    if (this.showDeleted) {
      uri += 'deleted';
    }

    return uri;
  }

  get paginationConfig(): PaginationInstance {
    this.configuration.rows = this.pagination.limit;

    return {
      id: 'custom',
      itemsPerPage: this.pagination.limit,
      currentPage: this.pagination.offset + 1,
      totalItems: this.pagination.count,
    };
  }

  updatePagerFromUri() {
    const params = new HttpParams({fromObject: this.route.snapshot.queryParams});

    this.pagination.offset = Number(params.get('page')) < 1 ? 0 : Number(params.get('page')) - 1;
    this.pagination.limit = Number(params.get('limit')) || this.pagination.limit;
    this.pagination.order = params.get('sort') || params.get('order') || this.config?.defaultSortColumn;
    this.pagination.direction = params.get('direction') || this.config?.defaultSortDirection;

    if (this.pagination.order) {
      this.configuration.defaultSortColumn = this.pagination.order;
      this.configuration.defaultSortDirection = this.pagination.direction;
    }
  }

  eventEmitted($event: { event: string; value: any; data?: any }): void {
    const event = $event.value;
    console.debug($event);

    switch ($event.event) {
      case Event.onSearch:
        this.onTableSearch(event[0]);
        break;

      case Event.onPagination:
        this.viewportScroller.scrollToPosition([0, 0]);
        this.onNavigate({page: event.page, limit: event.limit});
        break;

      case Event.onColumnResize:
        this.tableService.saveConfig($event);
        break;

      case Event.onEnabled:
        this.onEnabled($event.value.enabled, $event.value.id)
        break;

      case Event.onOrder:
        this.pagination.direction = event.order ? event.order : this.pagination.direction;
        this.pagination.order = event.key ? event.key : this.pagination.order;
        this.pagination = {...this.pagination};
        this.onNavigate({order: event.key, direction: event.order});
        break;

      case Event.onCheckboxSelect:
        this.selectRow(event.row, event.isSelected);
        break;

      case Event.onSelectAll:
        for (const row of event.data) {
          this.selectRow(row, event.isSelected);
        }
        break;
    }
  }

  onPagination(pagination: PaginationRange): void {
    this.eventEmitted({event: Event.onPagination, value: pagination});
  }

  onNavigate(queryParams: Params, queryParamsHandling: QueryParamsHandling | null = 'merge') {
    this.router.navigate(['.'], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: queryParamsHandling
    });
  }

  onTableSearch(event: HeaderSearchEvent) {
    if (!event.value.length) {
      this.params = this.params.delete(event.key);
    } else {
      this.params = this.params.set(event.key, event.value);
    }

    const queryParams: Params = {};
    queryParams[event.key] = event.value.length ? event.value : null;
    queryParams['page'] = 1;
    this.pagination.offset = 0;
    this.onNavigate(queryParams);
  }

  resetSelected(): void {
    this.selected.clear();
    this.selectedRows.clear();
    this.updateSelected();
  }

  selectRow(row, value) {
    if (!value) {
      this.selectedRows.delete(row.id);
      this.selected.delete(row.id);
    } else {
      this.selectedRows.set(row.id, row);
      this.selected.add(row.id);
    }

    this.updateSelected();
  }

  updateSelected() {
  }

  onEnabled(enabled: boolean, id: number) {
  }
}
